<template>
    <div
        :class="componentClasses"
    >
        <div
            v-for="(option, index) in availableQuickOptions"
            :key="option.id.value"
            class="delayed-quick-options__option"
            :style="{ animationDelay: `${index * .1}s` }"
            @click="onOptionClicked(option)"
            @keydown.enter="onOptionClicked(option)"
        >
            {{ localizedQuickOptionShortMessage(option) }}
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue';
    import useConfiguration from '@/composables/useConfiguration';
    import useWidgetState, { State } from '@/composables/useWidgetState';
    import { type QuickOption, VisitorInteractionType } from '@keyvue/one-api-client';
    import { useI18n } from 'vue-i18n';
    import Chat from '@/chat/Chat';
    import useVisitorData from '@/composables/useVisitorData';

    const chat = Chat.getInstance();
    const { registerInteraction } = useVisitorData();
    const { assistent } = useConfiguration();
    const { state } = useWidgetState();
    const { locale } = useI18n();

    const availableQuickOptions = computed(() => assistent.value?.widget.quickOptions ?? []);

    export interface Props {
        alignFlexEnd: boolean,
    }

    const props = withDefaults(defineProps<Props>(), {
        alignFlexEnd: false,
    });

    function localizedQuickOptionShortMessage(option: QuickOption): string {
        return assistent.value?.behavior.englishSupportEnabled
            ? option.getShortMessageForLocale(locale.value)
            : option.shortMessage;
    }

    function localizedQuickOptionMessage(option: QuickOption): string {
        return assistent.value?.behavior.englishSupportEnabled
            ? option.getMessageForLocale(locale.value)
            : option.message;
    }

    const componentClasses = computed(() => {
        return {
            'delayed-quick-options': true,
            'delayed-quick-options--align-flex-end': props.alignFlexEnd,
        };
    });

    async function onOptionClicked(option: QuickOption) {
        if (!assistent.value) {
            return;
        }

        state.value = State.Opened;
        await chat.sendMessage(localizedQuickOptionMessage(option));
        await registerInteraction(VisitorInteractionType.QUICK_OPTION_CLICKED_OUTSIDE);
    }
</script>

<style scoped lang="scss">
    .delayed-quick-options {
        display: flex;
        flex-wrap: wrap;
        gap: .5rem;

        &--align-flex-end {
            justify-content: flex-end;
        }

        &__option {
            background-color: var(--color-widget);
            color: var(--color-widget-text);
            border-radius: .5rem;
            padding: 0.5rem;
            pointer-events: auto;
            cursor: pointer;
            text-wrap: nowrap;
        }
    }

    @media screen and (min-width: 768px) {
        .delayed-quick-options {
            flex-wrap: nowrap;
        }
    }
</style>
